import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import './styles.css';

const COUNTDOWN_DURATION = 90;
const COUNTDOWN_INTERVAL = 1000;
const REFRESH_DELAY = 100;

export default function Countdown({ refresh, isLoadingData, setIsLoading }) {
    const { t } = useTranslation('StudyList');
    const navigate = useNavigate();
    const { search } = useLocation();
    const [count, setCount] = useState(COUNTDOWN_DURATION);
    const isLoadingRef = useRef(isLoadingData);

    useEffect(() => {
        isLoadingRef.current = isLoadingData;
    }, [isLoadingData]);

    useEffect(() => {
        if (isLoadingData) {
            setCount(COUNTDOWN_DURATION);
        }
    }, [isLoadingData]);

    const generateRandomString = useCallback(() => {
        return Math.random().toString(36).slice(2, 5);
    }, []);

    const handleNavigateAndRefresh = useCallback(() => {
        const searchParams = new URLSearchParams(search);
        searchParams.set('update', generateRandomString());

        setTimeout(() => {
            refresh();
            navigate(
                { pathname: '/', search: searchParams.toString() },
                { replace: true }
            );
            setIsLoading(true)
        }, REFRESH_DELAY);
    }, [search, navigate, refresh, generateRandomString]);

    useEffect(() => {
        const countdown = () => {
            setCount(prevCount => {
                if (isLoadingRef.current) {
                    return COUNTDOWN_DURATION;
                }

                if (prevCount === 1) {
                    handleNavigateAndRefresh();
                    return COUNTDOWN_DURATION;
                }

                return Math.max(0, prevCount - 1);
            });
        };

        const intervalId = setInterval(countdown, COUNTDOWN_INTERVAL);

        return () => clearInterval(intervalId);
    }, [handleNavigateAndRefresh]);

    const tooltipTitle = useMemo(
        () => t('Wait to update studies'),
        [t]
    );

    const shouldShowCountdown = !isLoadingData;

    return (
        <div
            id="countdown"
            title={tooltipTitle}
            className="countdown-container"
        >
            {shouldShowCountdown && (
                <>
                    <div id="countdown-number" aria-live="polite">
                        {count}
                    </div>
                    <img
                        style={{ position: 'absolute', top: 0 }}
                        src="public/loading.svg"
                        width={100}
                        height={100}
                        alt="Countdown"
                        loading="lazy"
                    />
                </>
            )}
        </div>
    );
}
